import React, { Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    CardHeader,
    Grid2 as Grid,
    Grid2Props as GridProps,
    IconButton,
    Skeleton,
    Tooltip,
    TooltipProps,
    Typography,
    styled,
    useTheme,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiHelpCircleOutline, mdiInformationOutline } from '@mdi/js';

import { useAppDispatch, useAppSelector } from '../state/hooks';
import {
    fetchBottomBalanceAreas,
    fetchBottomDensityAreas,
    fetchBottomEmptyMilesAreas,
    fetchTopBalanceAreas,
    fetchTopDensityAreas,
} from '../state/features/areaHealthSlice';
import {
    fetchNetworkAreas,
    fetchNetworkKpis,
} from '../state/features/networkHealthSlice';
import { privateRoutes } from '../routes/appRouteList';
import { ApiStatus, Grade } from '../utils/enums';
import { formatCurrency } from '../utils/formattingUtils';
import { getGradeColor } from '../utils/styleUtils';
import Hidden from '../components/layout/hidden';
import GradeLabel from '../components/Labels/GradeLabel';
import BalanceMap from '../components/Map/BalanceMap';
import DensityMap from '../components/Map/DensityMap';
import EmptyMilesMap from '../components/Map/EmptyMilesMap';
import LoadTimeCard from '../components/Cards/loadTimeCard';
import InTransitDelayCard from '../components/Cards/inTransitDelayCard';
import MPHCard from '../components/Cards/mphCard';

interface StyleProps extends GridProps {
    grade: Grade | null;
}

const classesPrefix = 'healthAssessment';

const classes = {
    pageRoot: `${classesPrefix}-pageRoot`,
    pageRootItem: `${classesPrefix}-pageRootItem`,
    revenueAmount: `${classesPrefix}-revenueAmount`,
    cardHeader: `${classesPrefix}-cardHeader`,
    cardHeaderContent: `${classesPrefix}-cardHeaderContent`,
    cardContent: `${classesPrefix}-cardContent`,
    helpText: `${classesPrefix}-helpText`,
    title: `${classesPrefix}-title`,
};

const StyledGrid = styled(
    Grid,
    {
        // This prevents passing the StyleProps down to the component
        shouldForwardProp: (prop) => { return prop !== 'grade'; },
    }
)<StyleProps>(({ grade, theme }) => {
    return {
        [`&.${classes.pageRoot}`]: {
            minHeight: '100%',
        },
        [`& .${classes.pageRootItem}`]: {
            overflow: 'auto',
            padding: theme.spacing(2),
        },
        [`& .${classes.revenueAmount}`]: {
            color: getGradeColor(grade, theme),
        },
        [`& .${classes.cardHeader}`]: {
            paddingBottom: theme.spacing(0),
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: theme.palette.action.hover,
            },
        },
        [`& .${classes.cardHeaderContent}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        [`& .${classes.cardContent}`]: {
            paddingTop: theme.spacing(0),
        },
    };
});

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => {
    return <Tooltip {...props} classes={{ popper: className }} />;
})(() => {
    return {
        [`& .${classes.helpText}`]: {
            fontSize: '0.75rem',
        },
        [`& .${classes.title}`]: {
            fontWeight: 700,
        },
    };
});

const HealthAssessment = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const navigate = useNavigate();

    const networkKpisApiStatus = useAppSelector((state) => { return state.networkHealth.networkKpisApiStatus; });
    const networkKpis = useAppSelector((state) => { return state.networkHealth.networkKpis; });

    const networkAreasApiStatus = useAppSelector((state) => { return state.networkHealth.networkAreasApiStatus; });
    const networkAreas = useAppSelector((state) => { return state.networkHealth.networkAreas; });

    const bottomBalanceAreasApiStatus = useAppSelector((state) => { return state.areaHealth.bottomBalanceAreasApiStatus; });
    const bottomBalanceAreas = useAppSelector((state) => { return state.areaHealth.bottomBalanceAreas; });
    const topBalanceAreasApiStatus = useAppSelector((state) => { return state.areaHealth.topBalanceAreasApiStatus; });
    const topBalanceAreas = useAppSelector((state) => { return state.areaHealth.topBalanceAreas; });

    const bottomDensityAreasApiStatus = useAppSelector((state) => { return state.areaHealth.bottomDensityAreasApiStatus; });
    const bottomDensityAreas = useAppSelector((state) => { return state.areaHealth.bottomDensityAreas; });
    const topDensityAreasApiStatus = useAppSelector((state) => { return state.areaHealth.topDensityAreasApiStatus; });
    const topDensityAreas = useAppSelector((state) => { return state.areaHealth.topDensityAreas; });

    const bottomEmptyMilesAreasApiStatus = useAppSelector((state) => { return state.areaHealth.bottomEmptyMilesAreasApiStatus; });
    const bottomEmptyMilesAreas = useAppSelector((state) => { return state.areaHealth.bottomEmptyMilesAreas; });

    useEffect(() => {
        dispatch(fetchNetworkKpis());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchNetworkAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchBottomBalanceAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchTopBalanceAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchBottomDensityAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchTopDensityAreas());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchBottomEmptyMilesAreas());
    }, [dispatch]);

    const overallGradeTooltip = (
        <Typography variant='body2' className={classes.helpText}>
            <span>The </span>
            <span className={classes.title}>Overall Grade</span>
            <span> for the network is an average of the Health Assessment grades for area, shipper, and consignee metrics.</span>
        </Typography>
    );

    const handleBalanceMapCardClick = (): void => {
        navigate(privateRoutes.areaHealth, {
            state: { showBalance: true, showDensity: false, showEmptyMiles: false },
        });
    };

    const handleDensityMapCardClick = (): void => {
        navigate(privateRoutes.areaHealth, {
            state: { showBalance: false, showDensity: true, showEmptyMiles: false },
        });
    };

    const handleEmptyMilesMapCardClick = (): void => {
        navigate(privateRoutes.areaHealth, {
            state: { showBalance: false, showDensity: false, showEmptyMiles: true },
        });
    };

    return (
        <StyledGrid
            container
            className={classes.pageRoot}
            sx={{
                justifyContent: 'flex-start',
                flexDirection: 'column',
            }}
            grade={networkKpis.carrier_network_grade}
        >
            <div className={classes.pageRootItem}>
                <Grid container spacing={2} direction='row'>
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <Grid
                                    container
                                    spacing={4}
                                    direction='row'
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <Hidden direction='only' breakpoint='xs'>
                                        <Grid>
                                            <StyledTooltip title={overallGradeTooltip}>
                                                <GradeLabel
                                                    apiStatus={networkKpisApiStatus}
                                                    grade={networkKpis.carrier_network_grade}
                                                    variant='h1'
                                                />
                                            </StyledTooltip>
                                        </Grid>
                                    </Hidden>
                                    <Grid>
                                        <Typography variant='h4' component='div'>
                                            <span>Network Health Assessment</span>
                                            <span>
                                                <Tooltip title='Some data values may have been removed from calculations due to data quality issues'>
                                                    <IconButton>
                                                        <Icon path={mdiInformationOutline} size={1} />
                                                    </IconButton>
                                                </Tooltip>
                                            </span>
                                        </Typography>
                                        <Hidden direction='up' breakpoint='sm'>
                                            <StyledTooltip title={overallGradeTooltip}>
                                                <GradeLabel
                                                    apiStatus={networkKpisApiStatus}
                                                    grade={networkKpis.carrier_network_grade}
                                                    variant='h1'
                                                />
                                            </StyledTooltip>
                                        </Hidden>
                                        <Typography variant='h6'>Revenue (Last 30 days)</Typography>
                                        <Typography variant='h5' className={classes.revenueAmount}>
                                            {
                                                networkKpisApiStatus === ApiStatus.Idle || networkKpisApiStatus === ApiStatus.Loading ?
                                                    <Skeleton width={150} /> :
                                                    formatCurrency(networkKpis.linehaul_revenue_usd_amount)
                                            }
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            lg: 4,
                        }}
                    >
                        <Card>
                            <CardHeader
                                classes={{
                                    root: classes.cardHeader,
                                    content: classes.cardHeaderContent,
                                }}
                                title={
                                    <Fragment>
                                        <Typography variant='h4'>Balance</Typography>
                                        <GradeLabel
                                            apiStatus={networkAreasApiStatus}
                                            grade={networkAreas.areas_health.area_daily_balance_grade}
                                            variant='h2'
                                        />
                                    </Fragment>
                                }
                                disableTypography
                                action={
                                    <StyledTooltip
                                        title={
                                            <div>
                                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                                    <span className={classes.title}>Balance</span>
                                                    <span> is the difference between an area&apos;s count of daily inbound and outbound orders and assumes zero is ideal.</span>
                                                </Typography>
                                                <Typography variant='body2' className={classes.helpText}>
                                                    Grades are based on the absolute value of the average balance of all areas in the network:
                                                </Typography>
                                                <ul>
                                                    <li>
                                                        <span className={classes.title}>A</span>
                                                        <span> = Less than 2</span>
                                                    </li>
                                                    <li>
                                                        <span className={classes.title}>C</span>
                                                        <span> = Between 2 and 5</span>
                                                    </li>
                                                    <li>
                                                        <span className={classes.title}>E</span>
                                                        <span> = Greater than 5</span>
                                                    </li>
                                                </ul>
                                                <Typography variant='body2' className={classes.helpText}>
                                                    The map displays the top 5 and bottom 5 areas by average daily balance.
                                                </Typography>
                                            </div>
                                        }
                                    >
                                        <IconButton size='small' color='primary'>
                                            <Icon path={mdiHelpCircleOutline} size='1rem' />
                                        </IconButton>
                                    </StyledTooltip>
                                }
                                onClick={handleBalanceMapCardClick}
                            />

                            <CardContent className={classes.cardContent}>
                                <BalanceMap
                                    isFetchingData={
                                        bottomBalanceAreasApiStatus === ApiStatus.Idle || bottomBalanceAreasApiStatus === ApiStatus.Loading ||
                                        topBalanceAreasApiStatus === ApiStatus.Idle || topBalanceAreasApiStatus === ApiStatus.Loading
                                    }
                                    balanceAreas={[...bottomBalanceAreas.areas, ...topBalanceAreas.areas]}
                                    theme={theme}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            lg: 4,
                        }}
                    >
                        <Card>
                            <CardHeader
                                classes={{
                                    root: classes.cardHeader,
                                    content: classes.cardHeaderContent,
                                }}
                                title={
                                    <Fragment>
                                        <Typography variant='h4'>Density</Typography>
                                        <GradeLabel
                                            apiStatus={networkAreasApiStatus}
                                            grade={networkAreas.areas_health.area_daily_density_grade}
                                            variant='h2'
                                        />
                                    </Fragment>
                                }
                                disableTypography
                                action={
                                    <StyledTooltip
                                        title={
                                            <div>
                                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                                    <span className={classes.title}>Density</span>
                                                    <span> is the count of daily inbound orders to an area.</span>
                                                </Typography>
                                                <Typography variant='body2' className={classes.helpText}>
                                                    The grade thresholds for density are dynamically calculated based on the operating data set&apos;s maximum daily density for the past 30 days:
                                                </Typography>
                                                <ul>
                                                    <li>
                                                        <span className={classes.title}>A</span>
                                                        <span> = 75% or more of the maximum daily density</span>
                                                    </li>
                                                    <li>
                                                        <span className={classes.title}>B</span>
                                                        <span> = 50% to 75% of the maximum daily density</span>
                                                    </li>
                                                    <li>
                                                        <span className={classes.title}>D</span>
                                                        <span> = 25% to 50% of the maximum daily density</span>
                                                    </li>
                                                    <li>
                                                        <span className={classes.title}>E</span>
                                                        <span> = Less than 25% of the maximum daily density or an average daily density less than 2</span>
                                                    </li>
                                                </ul>
                                                <Typography variant='body2' className={classes.helpText}>
                                                    The map displays the top 5 and bottom 5 areas by average daily density.
                                                </Typography>
                                            </div>
                                        }
                                    >
                                        <IconButton size='small' color='primary'>
                                            <Icon path={mdiHelpCircleOutline} size='1rem' />
                                        </IconButton>
                                    </StyledTooltip>
                                }
                                onClick={handleDensityMapCardClick}
                            />

                            <CardContent className={classes.cardContent}>
                                <DensityMap
                                    isFetchingData={
                                        bottomDensityAreasApiStatus === ApiStatus.Idle || bottomDensityAreasApiStatus === ApiStatus.Loading ||
                                        topDensityAreasApiStatus === ApiStatus.Idle || topDensityAreasApiStatus === ApiStatus.Loading
                                    }
                                    densityAreas={[...bottomDensityAreas.areas, ...topDensityAreas.areas]}
                                    theme={theme}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            lg: 4,
                        }}
                    >
                        <Card>
                            <CardHeader
                                classes={{
                                    root: classes.cardHeader,
                                    content: classes.cardHeaderContent,
                                }}
                                title={
                                    <Fragment>
                                        <Typography variant='h4'>Empty Mile %</Typography>
                                        <GradeLabel
                                            apiStatus={networkAreasApiStatus}
                                            grade={networkAreas.areas_health.area_empty_miles_percentage_grade}
                                            variant='h2'
                                        />
                                    </Fragment>
                                }
                                disableTypography
                                action={
                                    <StyledTooltip
                                        title={
                                            <div>
                                                <Typography variant='body2' gutterBottom className={classes.helpText}>
                                                    <span className={classes.title}>Empty Mile %</span>
                                                    <span> is the quotient of order empty miles and order total miles expressed as a percentage.</span>
                                                </Typography>
                                                <Typography variant='body2' className={classes.helpText}>
                                                    The grade thresholds for Empty Miles are based on the percent of empty miles:
                                                </Typography>
                                                <ul>
                                                    <li>
                                                        <span className={classes.title}>A</span>
                                                        <span> = Less than 5%</span>
                                                    </li>
                                                    <li>
                                                        <span className={classes.title}>C</span>
                                                        <span> = Between 5% and 9%</span>
                                                    </li>
                                                    <li>
                                                        <span className={classes.title}>E</span>
                                                        <span> = Greater than 9%</span>
                                                    </li>
                                                </ul>
                                                <Typography variant='body2' className={classes.helpText}>
                                                    The map displays the bottom 5 outbound lanes of the 5 areas with the greatest Empty Mile %.
                                                </Typography>
                                            </div>
                                        }
                                    >
                                        <IconButton size='small' color='primary'>
                                            <Icon path={mdiHelpCircleOutline} size='1rem' />
                                        </IconButton>
                                    </StyledTooltip>
                                }
                                onClick={handleEmptyMilesMapCardClick}
                            />

                            <CardContent className={classes.cardContent}>
                                <EmptyMilesMap
                                    isFetchingData={
                                        bottomEmptyMilesAreasApiStatus === ApiStatus.Idle || bottomEmptyMilesAreasApiStatus === ApiStatus.Loading
                                    }
                                    emptyMilesAreas={bottomEmptyMilesAreas.areas}
                                    theme={theme}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            lg: 4,
                        }}
                    >
                        <LoadTimeCard />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            lg: 4,
                        }}
                    >
                        <InTransitDelayCard />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            lg: 4,
                        }}
                    >
                        <MPHCard />
                    </Grid>
                </Grid>
            </div>
        </StyledGrid>
    );
};

export default HealthAssessment;
